import type { TiebreakStepView } from '../leagues/tiebreak-note';
import type {
  MatchItem,
  MatchPlayer,
  MatchRating,
  MatchRules,
  ScoringGroup,
  ScoringSystem,
  SetEntryOptions,
  SetScore,
  StandingRow,
} from '../leagues/scoring-types';

export interface SwissEntrant {
  id: number;
  displayName: string;
}

export interface SwissSide {
  id: number;
  player: SwissEntrant | null;
  partner: SwissEntrant | null;
  rating: number | null;
}

export interface SwissMatch {
  id: number;
  round: number;
  status: string;
  sets: string | null;
  winnerRegistrationId: number | null;
  registration1Id: number | null;
  registration2Id: number | null;
  registration1: SwissSide | null;
  registration2: SwissSide | null;
  bye: boolean;
  // Szacunek z API: delta rozegranego meczu albo widełki „gdy wygra / gdy przegra".
  rating?: MatchRating | null;
}

export interface SwissRound {
  round: number;
  complete: boolean;
  matches: SwissMatch[];
}

export interface SwissStandingRow {
  registrationId: number;
  position: number;
  name: string;
  points: number;
  wins: number;
  draws: number;
  losses: number;
  setsWon: number;
  setsLost: number;
  smallPoints: number;
  smallPointsLost: number;
  played: number;
  byes: number;
  buchholz: number;
  rating: number | null;
  ratingDelta?: number | null;
  decidedBy?: StandingRow['decidedBy'];
}

export interface SwissView {
  tournament: { id: number; name: string; status: string; roundCount: number | null };
  category: { id: number; name: string; discipline: string };
  rules: MatchRules;
  setEntry: SetEntryOptions;
  scoring: ScoringSystem;
  tiebreakSteps: TiebreakStepView[];
  entrantCount: number;
  // Ile z delty ELO niesie mecz tego turnieju — do etykiety, liczba przychodzi z API.
  ratingWeight?: number;
  rounds: SwissRound[];
  standings: SwissStandingRow[];
}

export const BYE_LABEL = 'pauza';

function sideLabel(side: SwissSide | null): string {
  if (!side?.player) {
    return '—';
  }

  return side.partner ? `${side.player.displayName} / ${side.partner.displayName}` : side.player.displayName;
}

function sidePlayers(side: SwissSide | null): MatchPlayer[] {
  if (!side?.player) {
    return [];
  }

  const playerIds = [side.player.id, side.partner?.id].filter((id): id is number => id != null);

  return [{ memberId: side.id, name: sideLabel(side), playerIds }];
}

function parsedSets(sets: string | null): SetScore[] {
  if (!sets) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(sets);

    return Array.isArray(parsed) ? (parsed as SetScore[]) : [];
  } catch {
    return [];
  }
}

function winnerSideOf(match: SwissMatch): MatchItem['winnerSide'] {
  if (match.winnerRegistrationId === null) {
    return null;
  }

  return match.winnerRegistrationId === match.registration1Id ? 'SIDE1' : 'SIDE2';
}

function matchRatingOf(match: SwissMatch): MatchItem['rating'] {
  if (match.rating) {
    return match.rating;
  }

  const rating1 = match.registration1?.rating ?? null;
  const rating2 = match.registration2?.rating ?? null;

  if (rating1 === null || rating2 === null) {
    return null;
  }

  return { side1: { rating: rating1 }, side2: { rating: rating2 }, appliedDelta: null };
}

function matchItemOf(match: SwissMatch, view: SwissView): MatchItem {
  return {
    id: match.id,
    status: match.status,
    groupName: null,
    runNumber: match.round,
    rules: view.rules,
    setEntry: view.setEntry,
    player1Name: sideLabel(match.registration1),
    player2Name: match.bye ? BYE_LABEL : sideLabel(match.registration2),
    side1: sidePlayers(match.registration1),
    side2: match.bye ? [] : sidePlayers(match.registration2),
    sets: parsedSets(match.sets),
    winnerSide: winnerSideOf(match),
    rating: matchRatingOf(match),
  };
}

function standingRowOf(row: SwissStandingRow, view: SwissView): StandingRow {
  const side = view.rounds
    .flatMap((round) => round.matches)
    .flatMap((match) => [match.registration1, match.registration2])
    .find((candidate) => candidate?.id === row.registrationId);

  return {
    rank: row.position,
    leaguePlayerId: row.registrationId,
    playerIds: sidePlayers(side ?? null)[0]?.playerIds ?? [],
    name: row.name,
    played: row.played,
    wins: row.wins,
    draws: row.draws,
    losses: row.losses,
    setsWon: row.setsWon,
    setsLost: row.setsLost,
    points: row.points,
    smallPoints: row.smallPoints,
    smallPointsLost: row.smallPointsLost,
    buchholz: row.buchholz,
    placementPoints: 0,
    rating: row.rating,
    ratingDelta: row.ratingDelta ?? undefined,
    decidedBy: row.decidedBy ?? null,
  };
}

export function swissMatchesOf(view: SwissView): MatchItem[] {
  return view.rounds.flatMap((round) => round.matches.map((match) => matchItemOf(match, view)));
}

export function swissGroupOf(view: SwissView): ScoringGroup {
  const matches = swissMatchesOf(view);

  return {
    id: view.category.id,
    name: null,
    rules: view.rules,
    scoring: view.scoring,
    tiebreak: {
      order: [],
      custom: false,
      hint: view.tiebreakSteps.map((step) => step.label).join(' → '),
      steps: view.tiebreakSteps,
    },
    matchCount: matches.length,
    playedCount: matches.filter((match) => match.winnerSide !== null || match.sets.length > 0).length,
    standings: view.standings.map((row) => standingRowOf(row, view)),
  };
}
